/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useRef, useState } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type HeaderElement,
	type ParsedElement,
	type SectionElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import { OneColumnLayout } from './section-layout/one-column-layout';

type FoldedStyleProps = {
	section: SectionElement;
	header: HeaderElement | undefined;
	content: ParsedElement[];
	children?: React.ReactNode;
};

const unboundedStyles = unboundedCssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		transition: 'background-color 0.2s ease',
		width: '100%',
	},
	contentWrapper: {
		overflow: 'hidden',
		transition: 'max-height 0.3s ease',
	},
	contentExpanded: {
		maxHeight: '500px',
	},
	contentCollapsed: {
		maxHeight: '22px',
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 1,
	},
	leftIndentStyles: {
		paddingLeft: token('space.300'),
	},
});

const styles = cssMap({
	divider: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
});

export const DynamicLayoutFoldableSection = ({
	section,
	header,
	content,
	children,
}: FoldedStyleProps): React.JSX.Element => {
	const containerRef = useRef(null);
	const [isFolded, setIsFolded] = useState<boolean>(section.attributes?.folded === '1');
	const [hasMoreThanOneLine, setHasMoreThanOneLine] = useState(false);
	const hasMoreThanOneColumnOrRow =
		section.attributes?.layout === 'two-columns' || section.attributes?.layout === 'two-rows';
	const sectionKey = `${section.attributes?.layout || 'default'}-${header?.children || ''}`;

	useEffect(() => {
		const checkHasMoreThanOneLine = () => {
			if (containerRef.current) {
				const { scrollHeight, clientHeight, scrollWidth, clientWidth } = containerRef.current;
				setHasMoreThanOneLine(scrollHeight > clientHeight || scrollWidth > clientWidth);
			}
		};

		checkHasMoreThanOneLine();
	}, [content, children]);

	return (
		<div css={unboundedStyles.root} data-testid="dynamic-layout-foldable-section">
			<Inline alignBlock="center">
				{(hasMoreThanOneColumnOrRow || hasMoreThanOneLine) && (
					<IconButton
						icon={isFolded ? ChevronDownIcon : ChevronUpIcon}
						label={`${isFolded ? 'expand' : 'collapsed'} section`}
						appearance="subtle"
						spacing="compact"
						onClick={() => setIsFolded(!isFolded)}
						testId={isFolded ? 'expand-button' : 'collapse-button'}
						aria-expanded={!isFolded}
					/>
				)}
				{header && header.attributes?.show !== 'false' && content && (
					<Text weight="bold">{header && (header.children as string)}</Text>
				)}
			</Inline>
			<div
				ref={containerRef}
				css={[
					unboundedStyles.contentWrapper,
					isFolded ? unboundedStyles.contentCollapsed : unboundedStyles.contentExpanded,
					hasMoreThanOneLine && unboundedStyles.leftIndentStyles,
				]}
			>
				{isFolded ? (
					<OneColumnLayout key={`${sectionKey}-collapsed`} elements={content} />
				) : (
					<Box key={`${sectionKey}-expanded`}>{children}</Box>
				)}
			</div>
			<Box xcss={styles.divider} />
		</div>
	);
};
